import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link as MuiLink,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Divider,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { Save as SaveIcon, Delete as DeleteIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { 
  getExpoChannel, 
  updateExpoChannel, 
  deleteExpoChannel, 
  createExpoChannel,
  getExpoReleases
} from '../../services/expoUpdates';
import { appApi } from '../../services/api';
import type { ExpoChannel, ExpoRelease } from '../../models/expo';
import type { App } from '../../models';

const ExpoChannelDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isNew = id === 'create';
  const navigate = useNavigate();

  const [channel, setChannel] = useState<Partial<ExpoChannel>>({
    name: '',
    description: '',
  });
  const [releases, setReleases] = useState<ExpoRelease[]>([]);
  const [apps, setApps] = useState<App[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);

  // Load data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Fetch available apps for the dropdown
        const appsResponse = await appApi.getAll();
        const appsList = Array.isArray(appsResponse.results) ? appsResponse.results : appsResponse;
        setApps(appsList);

        if (!isNew) {
          const channelId = parseInt(id!);
          const [channelData, releasesData] = await Promise.all([
            getExpoChannel(channelId),
            getExpoReleases(channelId),
          ]);
          setChannel(channelData);
          setReleases(releasesData);
        } else if (appsList.length > 0) {
          // Pre-select first app for convenience in create mode
          setChannel((prev) => ({ ...prev, app: appsList[0]?.id }));
        }
      } catch (err) {
        console.error('Failed to load data:', err);
        setError('Failed to load data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [id, isNew]);

  // Handle form changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setChannel((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  
  // Handle select changes
  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    setChannel((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle form submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    
    try {
      if (isNew) {
        await createExpoChannel(channel);
      } else {
        await updateExpoChannel(parseInt(id!), channel);
      }
      navigate('/expo-updates');
    } catch (err) {
      console.error('Failed to save channel:', err);
      setError('Failed to save channel. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Handle channel delete
  const handleDelete = async () => {
    setDeleteDialogOpen(false);
    setSaving(true);
    setError(null);
    
    try {
      await deleteExpoChannel(parseInt(id!));
      navigate('/expo-updates');
    } catch (err) {
      console.error('Failed to delete channel:', err);
      setError('Failed to delete channel. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Release table columns
  const releaseColumns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'version', headerName: 'Version', width: 120 },
    { field: 'runtime_version', headerName: 'Runtime Version', width: 150 },
    { 
      field: 'platform', 
      headerName: 'Platform', 
      width: 100,
      renderCell: (params) => {
        return params.value === 'all' ? 'All Platforms' : params.value;
      }
    },
    { 
      field: 'status', 
      headerName: 'Status', 
      width: 120,
      renderCell: (params) => {
        const status = params.value;
        let color = '';
        switch(status) {
          case 'published':
            color = '#4caf50'; // Green
            break;
          case 'draft':
            color = '#ffc107'; // Amber
            break;
          case 'rolled-back':
            color = '#f44336'; // Red
            break;
        }
        
        return (
          <Box 
            sx={{ 
              backgroundColor: color,
              color: 'white',
              borderRadius: '4px',
              padding: '3px 10px',
              textTransform: 'capitalize'
            }}
          >
            {status}
          </Box>
        );
      }
    },
    { field: 'created_at', headerName: 'Created', width: 200 },
  ];

  return (
    <Box sx={{ width: '100%' }}>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <MuiLink component={Link} to="/">
          Home
        </MuiLink>
        <MuiLink component={Link} to="/expo-updates">
          Expo Updates
        </MuiLink>
        <Typography color="text.primary">
          {isNew ? 'Create Channel' : `Edit Channel: ${channel.name}`}
        </Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {isNew ? 'Create New Channel' : `Edit Channel: ${channel.name}`}
        </Typography>
        <Box>
          <Button 
            variant="outlined" 
            startIcon={<ArrowBackIcon />}
            component={Link}
            to="/expo-updates"
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          {!isNew && (
            <Button 
              variant="outlined" 
              color="error" 
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteDialogOpen(true)}
              sx={{ mr: 2 }}
            >
              Delete
            </Button>
          )}
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<SaveIcon />}
            onClick={handleSubmit}
            disabled={saving}
          >
            Save
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box component="form" onSubmit={handleSubmit}>
          <Paper sx={{ p: 3, mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Channel Information
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel id="app-label">Application</InputLabel>
                  <Select
                    labelId="app-label"
                    id="app"
                    name="app"
                    value={channel.app || ''}
                    label="Application"
                    onChange={handleSelectChange}
                  >
                    {apps.map((app) => (
                      <MenuItem key={app.id} value={app.id}>
                        {app.name} ({app.type})
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>Select the app this update channel belongs to</FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  required
                  fullWidth
                  id="name"
                  name="name"
                  label="Channel Name"
                  value={channel.name}
                  onChange={handleInputChange}
                  helperText="A unique name for this update channel (e.g., production, beta)"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  id="description"
                  name="description"
                  label="Description"
                  value={channel.description}
                  onChange={handleInputChange}
                  helperText="Optional description about this channel's purpose"
                />
              </Grid>
            </Grid>
          </Paper>

          {!isNew && releases.length > 0 && (
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Releases in This Channel
                </Typography>
                <Button 
                  variant="outlined"
                  component={Link}
                  to={`/expo-updates/releases/create?channel=${id}`}
                >
                  New Release
                </Button>
              </Box>
              <Divider sx={{ mb: 3 }} />
              
              <Box sx={{ height: 400 }}>
                <DataGrid
                  rows={releases}
                  columns={releaseColumns}
                  initialState={{
                    pagination: { paginationModel: { pageSize: 5 } },
                    sorting: {
                      sortModel: [{ field: 'created_at', sort: 'desc' }],
                    },
                  }}
                  pageSizeOptions={[5, 10, 25]}
                  onRowClick={(params) => navigate(`/expo-updates/releases/${params.row.id}`)}
                  sx={{ cursor: 'pointer' }}
                />
              </Box>
            </Paper>
          )}
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Channel</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the channel "{channel.name}"? This action cannot be undone.
            {releases.length > 0 && (
              <strong> Warning: This channel has {releases.length} releases that will also be deleted.</strong>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExpoChannelDetail;
